import { FunctionComponent, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Flex } from '@rebass/emotion';
import { useIntl } from 'react-intl';
import { useTheme } from 'emotion-theming';
import { AppTheme } from '@app/styles/theme';
import useLocaleWithFallback from '@app/shop/hooks/useLocaleWithFallback';
import Loader from '@app/components/Loader';
import NotFound from '@app/components/NotFound';
import { pushData, pushUserData } from '@app/shop/tracking';

import PricingPageHeader from '../components/PricingPageHeader';
import ProductLayout from '../components/ProductLayout';
import SupportedPaymentMethods from '../components/SupportedPaymentMethods';
import {
  PricingPage_content_pricing_pages,
  PricingPage_content_pricing_pages_layout_item_pricing_pages_single_layout as SingleLayout,
} from '../__generated__/PricingPage';
import { PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as Variant } from '../hook/__generated__/PricingPageShopProduct';
import { ContentWrapper, BottomContent } from './styles';
import messages from './SingleProduct.messages';
import ContentBlock from '../components/ContentBlock';
import useShopProduct from '../hook/useShopProduct';
import AdditionalInfo from './components/AdditionalInfo';
import { convertToEuro } from '../../../helpers';
export interface SingleProductLayoutProps
  extends Omit<PricingPage_content_pricing_pages, 'layout' | 'translations'> {
  layout: SingleLayout;
}

const SingleProductLayout: FunctionComponent<SingleProductLayoutProps> = ({
  layout,
  title,
  title_color,
}) => {
  const theme: AppTheme = useTheme();
  const intl = useIntl();
  const { language } = useParams<{
    language: string;
  }>();
  const { findTranslationByKeyWithFallback } = useLocaleWithFallback();

  const { show_retailer_button, content, product } = layout;
  const { loading, error, notFound, magentoProduct, user } = useShopProduct(
    product
  );
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const { links, system_requirements } = product ?? {};

  const variants =
    magentoProduct?.__typename === 'Magento2_ConfigurableProduct'
      ? magentoProduct.variants
      : [];

  const hasVariants = variants?.length;

  useEffect(() => {
    if (user?.country) {
      pushUserData({
        isAuthenticated: user?.isAuthenticated,
        country: user?.country,
        language,
        pageCategory: 'www',
      });

      pushData({
        google_tag_params: {
          pagetype: 'product',
          pname: product?.title,
          prodid: product?.sku,
        },
      });
    }
  }, [user?.country]);

  useEffect(() => {
    if (hasVariants) {
      const price = variants?.reduce((acc, item) => {
        const itemPrice =
          item?.product?.priceRange?.minimumPrice?.finalPrice?.value || 0;
        if ((acc && itemPrice && itemPrice < acc) || !acc) {
          acc = itemPrice;
        }

        return acc;
      }, 0);

      const convertedPrice = convertToEuro(
        price,
        variants?.[0]?.product?.priceRange?.minimumPrice?.finalPrice?.currency
      );

      pushData({
        ecommerce: {
          detail: {
            actionField: {
              list: 'Detail Page',
            },
            products: [
              {
                id: product?.sku,
                price: convertedPrice,
              },
            ],
          },
        },
      });

      pushData({
        event: 'funnel',
        step: 'product',
        products: [
          {
            productName: product?.title,
            productId: product?.sku,
            price: convertedPrice,
            category: '',
            subCategory: '',
          },
        ],
      });
    }
  }, [hasVariants]);

  if (loading) {
    return (
      <Flex py={4} justifyContent="center">
        <Loader color={theme.colors.lighterBlue} size={30} />
      </Flex>
    );
  }

  if (error) {
    throw new Error(
      'Unable to query shop products in single pricing page layout'
    );
  }

  if (
    notFound ||
    magentoProduct?.__typename !== 'Magento2_ConfigurableProduct' ||
    !hasVariants
  )
    return <NotFound />;

  const additional_information = findTranslationByKeyWithFallback({
    translations: product?.translations,
    key: 'additional_information',
  });

  const product_includes = findTranslationByKeyWithFallback({
    translations: product?.translations,
    key: 'product_includes',
  });

  const headerLinks = links?.map((link) => {
    const translations = link?.products_links_id?.translations;
    return {
      title: findTranslationByKeyWithFallback({
        translations,
        key: 'title',
      }),
      url: findTranslationByKeyWithFallback({
        translations,
        key: 'url',
      }),
    };
  });

  const systemRequirements = system_requirements?.reduce((acc, sys) => {
    const translations = sys?.system_requirements_id?.translations;
    return (
      acc +
      findTranslationByKeyWithFallback({
        translations,
        key: 'content',
      })
    );
  }, '');

  const bottomContent = content?.[0]?.bottom_content;

  return (
    <Box data-testid="pricing_pages_single_layout">
      <Box pb={4}>
        <PricingPageHeader
          title={title}
          bgColor={title_color}
          links={headerLinks}
        />
      </Box>
      <ContentWrapper px={4}>
        {product && (
          <ProductLayout
            product={product}
            magentoProduct={magentoProduct}
            showRetailerButton={show_retailer_button}
            onSelectVariant={setSelectedVariant}
          />
        )}
        <SupportedPaymentMethods />

        <AdditionalInfo
          contentInfo={additional_information}
          variantInfo={selectedVariant?.product?.additionalInfo}
        />

        {(product_includes || systemRequirements) && (
          <Flex justifyContent="space-between" flexWrap="wrap">
            {product_includes && (
              <ContentBlock
                title={intl.formatMessage(messages.productIncludesTitle)}
                innerHtml={product_includes}
                width={['100%', systemRequirements ? '48%' : '100%']}
              />
            )}
            {systemRequirements && (
              <ContentBlock
                data-testid="system-requirements"
                title={intl.formatMessage(messages.systemRequirementsTitle)}
                innerHtml={systemRequirements}
                width={['100%', product_includes ? '48%' : '100%']}
              />
            )}
          </Flex>
        )}

        {bottomContent && (
          <BottomContent dangerouslySetInnerHTML={{ __html: bottomContent }} />
        )}
      </ContentWrapper>
    </Box>
  );
};

export default SingleProductLayout;
